import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import axios, { AxiosError } from "axios"
import { useQuery, useQueryClient } from "react-query"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import {
     IConsultantDeclarationMensuelleModel,
     IConsultantDeclarationMensuelleReviewModel,
     IConsultantDeclarationMensuelleSuiviModel,
     IHttpErrorResponseModel,
} from "@common-models/*"
import {
     CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_INTERESSE_SIGNATURE,
     CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_RESP_VALIDATION,
     CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_SIEGE_DEMANDE_RECTIFICATION,
     CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_SIEGE_VALIDATION,
     CONST_CONSULTANT_STATUT_ST_PORTAGE,
     CONST_CONSULTANT_STATUT_ST_SOCIETE_SERVICE,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
     CONST_PERSONNEL_APPLICATION_ACCESS_MODULE_ADMINISTRATIF,
     CONST_USER_ROLE_RECRUTEUR,
} from "@common-constants/*"
import {
     IConsultantsDeclarationsListingContextPropsModel,
     IGetConsultantsDeclarationsDataItemResponseModel,
     IGetConsultantsDeclarationsRequestModel,
     IGetConsultantsDeclarationsResponseModel,
     IItemInModal,
} from "./core/_models"
import { getConsultantsDeclarationsListing, relanceConsultantsDeclarationActiviteRequest } from "./core/_requests"
import ConsultantsDeclarationsListingSearchBar from "./_ConsultantsDeclarationsListingSearchBar"
import ConsultantsDeclarationsListingList from "./_list/_ConsultantsDeclarationsListingList"
import ConsultantsDeclarationsListingFilters from "./_ConsultantsDeclarationsListingFilters"
import ConsultantDeclarationDetails from "../details/_ConsultantDeclarationDetails"
import MyModal from "@common-utils/MyModal"
import { getSelectedDeclarationPlusPreviousAndNextDeclaration } from "./core/helpers"
import { CONST_API_BUSINESS_CONSULTANT_DECLARATION_DOWNLOAD_ENDPOINT } from "../../../../../endpoints"
import { useAuth } from "../../../../../AppContext"
import ConsultantDeclarationSuivi from "../_ConsultantDeclarationSuivi"
import { IIsBulkSelectionEnabledPropsModel } from "../../../../../models"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import moment from "moment/moment"
import swal from "sweetalert2"
import useUser from "../../../../../hooks/useUser"
import { addSuiviDeclarationValidationResponsable, addSuiviDeclarationValidationSiege } from "../core/_requests"

// Wrapper's context
const ConsultantsDeclarationsListingContext = createContext<IConsultantsDeclarationsListingContextPropsModel>({} as IConsultantsDeclarationsListingContextPropsModel)
export const useConsultantsDeclarationsListingContext = () => useContext(ConsultantsDeclarationsListingContext)

const REACT_QUERY_KEY_GET_DECLARATIONS_PROD = "REACT_QUERY_KEY_GET_DECLARATIONS_PROD"
const ConsultantsDeclarationsListing = () => {
     const queryClient = useQueryClient()
     const navigate = useNavigate()
     const { currentUser } = useAuth()
     const { isAdministratif } = useUser()

     // Page states & URL parameters
     const location = useLocation() as {
          state: {
               etat?:
                    | "ETAT_ADMIN__EN_ATTENTE_RECEPTION"
                    | "ETAT_ADMIN__EN_COURS_RECTIFICATION"
                    | "ETAT_ADMIN__A_VALIDER"
                    | "ETAT_ADMIN__VALIDE_POUR_PAIE"
                    | "ETAT_NON_ADMIN__EN_ATTENTE_RECEPTION"
                    | "ETAT_NON_ADMIN__EN_COURS_RECTIFICATION"
                    | "ETAT_NON_ADMIN__A_ENVOYER_AU_SIEGE"
                    | "ETAT_NON_ADMIN__A_CONTROLER"
                    | "ETAT_NON_ADMIN__ENVOYE_AU_SIEGE"
                    | "ETAT_NON_ADMIN__VALIDE_POUR_PAIE"
               mois?: string
               contrat?: "salarie" | "st"
               keyword?: string
          }
     }
     const urlParams = new URLSearchParams(window.location.search)
     const urlParamsKeyword = urlParams.get("keyword")

     // Referencing both filters form and simple search bar form
     const filtersFormikRef = useRef<any>(null)
     const simpleSearchBarFormikRef = useRef<any>(null)

     const [areFiltersVisible, setAreFiltersVisible] = useState<boolean>(false)
     const [filters, setFilters] = useState<IGetConsultantsDeclarationsRequestModel>({
          page: 1,
          pageLength: 500,
          etat: location.state?.etat,
          contrat: location.state?.contrat,
          mois: !location.state?.mois && !urlParams.get("mois") ? "" : location.state?.mois || (urlParams.get("mois") as string),
          keyword: urlParamsKeyword || undefined,
     })
     const [isBulkSelectionEnabled, setIsBulkSelectionEnabled] = useState<IIsBulkSelectionEnabledPropsModel>({
          enabled: false,
          checkedElementsInPage: [],
          areAllElementsInAllPagesChecked: false,
     })
     const [itemInModal, setItemInModal] = useState<IItemInModal | null>(null)
     const [suiviInModal, setSuiviInModal] = useState<IConsultantDeclarationMensuelleModel>()
     const doesConnectedUserHaveAccessToModuleAdmin = (() => {
          return currentUser?.authorizedAppModules?.find(item => item === CONST_PERSONNEL_APPLICATION_ACCESS_MODULE_ADMINISTRATIF) !== undefined
     })()

     // Main listing query
     const listingQuery = useQuery<IGetConsultantsDeclarationsResponseModel, AxiosError>(
          REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
          async () => {
               return getConsultantsDeclarationsListing(filters)
                    .then(r => {
                         // Stop loaders (filters + simple search bar)
                         filtersFormikRef.current.setSubmitting(false)
                         simpleSearchBarFormikRef.current.setSubmitting(false)

                         return r.data
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data

                         // Set form errors in the filters fomr
                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) filtersFormikRef.current.setFieldError(key, error.errors[key])
                         }

                         // Stop loaders (filters + simple search bar)
                         filtersFormikRef.current.setSubmitting(false)
                         simpleSearchBarFormikRef.current.setSubmitting(false)

                         // Toast error
                         toast.error(error?.detail, { autoClose: false })

                         throw e
                    })
          },
          { enabled: false }
     )

     // Handles demande rectification
     function handleDemandeRectificationCallback(val: IConsultantDeclarationMensuelleSuiviModel) {
          // Update item in declarations list
          queryClient.setQueryData<IGetConsultantsDeclarationsResponseModel | undefined>(
               REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
               (prev: IGetConsultantsDeclarationsResponseModel | undefined) => {
                    if (prev?.data && prev.data.length > 0) {
                         const index = prev.data.findIndex(item => item.declaration?.id === itemInModal?.selectedItem.declaration?.id)
                         if (index >= 0) {
                              prev.data[index] = {
                                   ...prev.data[index],
                                   declaration: {
                                        ...prev.data[index].declaration,
                                        etat: val,
                                   },
                              }
                              return prev
                         }
                    }

                    return prev
               }
          )
          showNextItemToControlInModal()
     }

     // Handles review
     function handleReviewCallback(val: IConsultantDeclarationMensuelleReviewModel) {
          queryClient.setQueryData<IGetConsultantsDeclarationsResponseModel | undefined>(
               REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
               (prev: IGetConsultantsDeclarationsResponseModel | undefined) => {
                    if (prev?.data && prev.data.length > 0) {
                         const index = prev.data.findIndex(item => item.declaration?.id === itemInModal?.selectedItem.declaration?.id)

                         if (index >= 0) {
                              prev.data[index] = {
                                   ...prev.data[index],
                                   declaration: {
                                        ...prev.data[index].declaration,
                                        reviews: prev.data[index].declaration?.reviews ? [val, ...prev.data[index].declaration!.reviews!] : [val], // Ensure `reviews` is always an array
                                   },
                              }
                              return { ...prev } // Return a new object to trigger reactivity
                         }
                    }

                    return prev
               }
          )
          showNextItemToControlInModal()
     }

     function handleCancelReviewCallback(val: IConsultantDeclarationMensuelleReviewModel) {
          queryClient.setQueryData<IGetConsultantsDeclarationsResponseModel | undefined>(
               REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
               (prev: IGetConsultantsDeclarationsResponseModel | undefined) => {
                    if (prev?.data && prev.data.length > 0) {
                         const index = prev.data.findIndex(item => item.declaration?.id === itemInModal?.selectedItem.declaration?.id)

                         if (index >= 0) {
                              prev.data[index] = {
                                   ...prev.data[index],
                                   declaration: {
                                        ...prev.data[index].declaration,
                                        reviews: prev.data[index].declaration?.reviews
                                             ? prev.data[index].declaration!.reviews!.filter(review => review !== val) // Remove `val` from reviews array
                                             : [], // If no reviews, return an empty array
                                   },
                              }
                              return { ...prev } // Return a new object to trigger reactivity
                         }
                    }

                    return prev
               }
          )
          showNextItemToControlInModal()
     }

     // Handles validation demande de réctif par resp
     function handleValidationDemandeRegulCallback(val: IConsultantDeclarationMensuelleSuiviModel) {
          // Update item in declarations list
          queryClient.setQueryData<IGetConsultantsDeclarationsResponseModel | undefined>(
               REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
               (prev: IGetConsultantsDeclarationsResponseModel | undefined) => {
                    if (prev?.data && prev.data.length > 0) {
                         const index = prev.data.findIndex(item => item.declaration?.id === itemInModal?.selectedItem.declaration?.id)
                         if (index >= 0) {
                              prev.data[index] = {
                                   ...prev.data[index],
                                   declaration: {
                                        ...prev.data[index].declaration,
                                        etat: val,
                                   },
                              }
                              return prev
                         }
                    }

                    return prev
               }
          )
          showNextItemToControlInModal()
     }

     // Handles rejet demande de réctif par resp
     function handleRejetDemandeRegulCallback(val: IConsultantDeclarationMensuelleSuiviModel) {
          // Update item in declarations list
          queryClient.setQueryData<IGetConsultantsDeclarationsResponseModel | undefined>(
               REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
               (prev: IGetConsultantsDeclarationsResponseModel | undefined) => {
                    if (prev?.data && prev.data.length > 0) {
                         const index = prev.data.findIndex(item => item.declaration?.id === itemInModal?.selectedItem.declaration?.id)
                         if (index >= 0) {
                              prev.data[index] = {
                                   ...prev.data[index],
                                   declaration: {
                                        ...prev.data[index].declaration,
                                        etat: val,
                                   },
                              }
                              return prev
                         }
                    }

                    return prev
               }
          )
          showNextItemToControlInModal()
     }

     function showNextItemToControlInModal() {
          // Get index of the next controllable declaration
          const nextItemToControlIndex = listingQuery.data?.data.findIndex(item => {
               if (item.declaration?.id !== itemInModal?.selectedItem.declaration?.id) {
                    // Depending if connected user is from siège or BM
                    if (doesConnectedUserHaveAccessToModuleAdmin) {
                         return item.declaration?.etat?.type === CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_RESP_VALIDATION
                    } else if (currentUser?.roles?.includes(CONST_USER_ROLE_RECRUTEUR)) {
                         const connectedUserReview = item.declaration?.reviews?.find(item_ => item_.personnel?.id === currentUser?.personnel?.id && item_.deletedAt === null)

                         return connectedUserReview === undefined && [CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_INTERESSE_SIGNATURE].includes(item.declaration?.etat?.type as string)
                    } else {
                         return [CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_INTERESSE_SIGNATURE, CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_SIEGE_DEMANDE_RECTIFICATION].includes(
                              item.declaration?.etat?.type as string
                         )
                    }
               }
          }) as number

          // If there is one in the current page, show it in modal
          if (nextItemToControlIndex >= 0) {
               const nextItemToControl = listingQuery.data!.data[nextItemToControlIndex]
               if (nextItemToControl) {
                    setItemInModal(
                         getSelectedDeclarationPlusPreviousAndNextDeclaration(
                              nextItemToControl,
                              nextItemToControlIndex,
                              listingQuery.data!.data,
                              listingQuery.data!.page,
                              listingQuery.data!.totalPages
                         )
                    )
               }
          } else {
               // If there is no one left to control, hide modal
               setItemInModal(null)
               toast.info("Il ne reste plus aucune déclaration à contrôler.")
          }
     }

     // Handles download declarations
     function handleDownloadDeclarations(items: IGetConsultantsDeclarationsDataItemResponseModel[]) {
          toast.info("Téléchargement en cours ...", {
               autoClose: false,
          })
          axios.get(CONST_API_BUSINESS_CONSULTANT_DECLARATION_DOWNLOAD_ENDPOINT, {
               params: { ids: items.map(item => item.declaration?.id) },
               responseType: "blob",
          })
               .then(r => {
                    const fileName = items.length > 1 ? "déclarations.zip" : `declaration_${items[0].consultant!.prenomNom!.replace(/ /g, "_").toLowerCase()}.zip`
                    FileAndDriveHelper.downloadFile(r.data, fileName)

                    toast.dismiss()
                    toast.success(
                         items.length > 1
                              ? "Les déclarations séléctionnées ont bien été téléchargées."
                              : `La déclaration de ${items[0].consultant!.prenomNom} a bien été téléchargée.`
                    )
               })
               .catch(() => {
                    toast.dismiss()
                    toast.error("Un problème est survenu lors du téléchargement.", { autoClose: false })
               })
     }

     function isRelancable(item: IGetConsultantsDeclarationsDataItemResponseModel) {
          const declaration = item?.declaration

          if (!declaration || !declaration?.etat) return true

          if (isAdministratif()) {
               if (![CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_RESP_VALIDATION, CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_SIEGE_VALIDATION].includes(declaration.etat.type as string)) {
                    return true
               }
          } else {
               if (
                    ![
                         CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_INTERESSE_SIGNATURE,
                         CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_RESP_VALIDATION,
                         CONST_CONSULTANT_DECLARATION_SUIVI_TYPE_SIEGE_VALIDATION,
                    ].includes(declaration.etat.type as string)
               ) {
                    return true
               }
          }

          return false
     }

     function handleRelance(items: IGetConsultantsDeclarationsDataItemResponseModel[]) {
          const itemsRelancables = items.filter(item => isRelancable(item))

          if (itemsRelancables.length === 0) {
               toast.info(items.length > 1 ? "Aucun collaborateur ne peut être relancé parmi votre sélection." : "Ce collaborateur ne peut pas être relancé.")
               return
          }

          const date = moment(filters.mois)
          swal.fire({
               icon: "info",
               title: `Vous êtes sur le point de relancer ${
                    itemsRelancables.length > 1 ? ` ${itemsRelancables.length} déclarations` : `la déclaration de ${itemsRelancables[0]?.consultant.prenomNom}`
               }.`,
               text: `Le mail de relance sera uniquement envoyé si le collaborateur n'a pas finalisé ou signé sa déclaration. Voulez-vous continuer ?`,
               showCancelButton: true,
               confirmButtonText: "Relancer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async () => {
                    return relanceConsultantsDeclarationActiviteRequest({
                         ids: itemsRelancables.map(item => item.consultant.id as number),
                         month: date.format("MM"),
                         year: date.format("YYYY"),
                    })
                         .then(() => {
                              toast.success(itemsRelancables.length > 1 ? "Les mails de relance ont bien été envoyés." : "Le mail de relance a bien été envoyé.")
                              if (itemsRelancables.length > 1) {
                                   setIsBulkSelectionEnabled({
                                        enabled: false,
                                        checkedElementsInPage: [],
                                        areAllElementsInAllPagesChecked: false,
                                   })
                              }
                              // Update item in declarations list
                              queryClient.setQueryData<IGetConsultantsDeclarationsResponseModel | undefined>(
                                   REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
                                   (prev: IGetConsultantsDeclarationsResponseModel | undefined) => {
                                        if (prev?.data && prev.data.length > 0) {
                                             itemsRelancables.map(item_ => {
                                                  const index = prev.data.findIndex(item => item?.consultant.id === item_.consultant.id)
                                                  if (index >= 0) {
                                                       prev.data[index] = {
                                                            ...prev.data[index],
                                                            derniereRelance: {
                                                                 createdAt: moment(),
                                                                 relanceur: {
                                                                      id: currentUser?.personnel?.id,
                                                                      prenomNom: currentUser?.prenomNom,
                                                                 },
                                                            },
                                                       }
                                                  }
                                             })
                                        }

                                        return prev
                                   }
                              )
                         })
                         .catch(e => {
                              toast.error(e?.response?.data?.detail, { autoClose: false })
                         }).finally(() => {
                              setIsBulkSelectionEnabled({
                                   enabled: false,
                                   checkedElementsInPage: [],
                                   areAllElementsInAllPagesChecked: false,
                              })
                         })
               },
               allowOutsideClick: () => !swal.isLoading(),
          }).then()
     }

     // Handles validation
     function handleValidationCallback(vals: IConsultantDeclarationMensuelleSuiviModel[], showNextItem: boolean = true) {
          queryClient.setQueryData<IGetConsultantsDeclarationsResponseModel | undefined>(
               REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
               (prev: IGetConsultantsDeclarationsResponseModel | undefined) => {
                    if (!prev?.data || prev.data.length === 0) return prev

                    // On crée un nouveau tableau pour respecter l'immuabilité de React Query
                    const updatedData = prev.data.map(item => {
                         // On cherche si un suivi dans "vals" appartient à cette déclaration
                         const matchingNewSuivi = vals.find(v => v.declaration?.id === item.declaration?.id)

                         if (matchingNewSuivi) {
                              return {
                                   ...item,
                                   declaration: {
                                        ...item.declaration,
                                        etat: matchingNewSuivi,
                                   },
                              }
                         }
                         return item
                    })

                    return {
                         ...prev,
                         data: updatedData,
                    }
               }
          )
          if (showNextItem)  showNextItemToControlInModal()
     }

     function handleValidationResponsableOuSiegeEnMasse(items: IGetConsultantsDeclarationsDataItemResponseModel[]) {
          const request = isAdministratif() ? addSuiviDeclarationValidationSiege : addSuiviDeclarationValidationResponsable

          // Extraction des IDs pour l'appel API
          const ids = items.map(item => item.declaration?.id).filter(id => id !== undefined) as number[]

          if (ids.length === 0) return

          swal.fire({
               icon: "info",
               title: `Vous êtes sur le point de valider ${ids.length} déclaration(s) en masse`,
               text: "Voulez-vous continuer ?",
               input: "textarea",
               inputPlaceholder: " Commentaire(optionnel)",
               showCancelButton: true,
               confirmButtonText: "Confirmer",
               cancelButtonText: "Annuler",
               showLoaderOnConfirm: true,
               preConfirm: async (commentaire: string) => {
                    // Envoi de la requête en masse avec tous les IDs
                    return request(ids, commentaire)
                         .then(r => {
                              toast.success(`${ids.length} déclaration(s) ont été validées avec succès.`)
                              handleValidationCallback(r.data, false)
                         })
                         .catch(e => {
                              toast.error(e?.response?.data?.detail || "Une erreur est survenue lors de la validation en masse", { autoClose: false })
                         }).finally(() => {
                              setIsBulkSelectionEnabled({
                                   enabled: false,
                                   checkedElementsInPage: [],
                                   areAllElementsInAllPagesChecked: false,
                              })
                         })
               },
               allowOutsideClick: () => !swal.isLoading(),
          }).then()
     }

     useEffect(() => {
          // mois param is mandatory
          if (!location.state?.mois && !urlParams.get("mois")) {
               navigate(-1)
               return
          }

          // Re-fetch main listing query on filters change
          listingQuery.refetch().then()

          // Reset bulk selection
          setIsBulkSelectionEnabled({
               enabled: false,
               checkedElementsInPage: [],
               areAllElementsInAllPagesChecked: false,
          })
     }, [filters])

     return (
          <ConsultantsDeclarationsListingContext.Provider
               value={{
                    areFiltersVisible,
                    setAreFiltersVisible,
                    filters,
                    setFilters,
                    listingQuery,
                    filtersFormikRef,
                    simpleSearchBarFormikRef,
                    REACT_QUERY_KEY_GET_DECLARATIONS_PROD,
                    itemInModal,
                    setItemInModal,
                    suiviInModal,
                    setSuiviInModal,
                    isBulkSelectionEnabled,
                    setIsBulkSelectionEnabled,
                    handleDownloadDeclarations,
                    handleRelance,
                    handleValidationResponsableOuSiegeEnMasse,
                    isRelancable,
               }}
          >
               <div className={"row"}>
                    {/* Filters become visible when the filter icon is clicked */}
                    <div className={`col-xl-5 mb-4 mb-xl-0 ${!areFiltersVisible ? "d-none" : ""}`}>
                         <ConsultantsDeclarationsListingFilters />
                    </div>
                    <div className="col-xl col-xl-7">
                         {/* Simple bar search will be shown only if filters are not displayed */}
                         <div className={`mb-2 ${areFiltersVisible ? "d-none" : ""}`}>
                              <ConsultantsDeclarationsListingSearchBar />
                         </div>
                         <ConsultantsDeclarationsListingList />
                    </div>
               </div>

               {/* Modal with déclaration details */}
               {itemInModal && (
                    <MyModal
                         title={<span>Déclaration de {itemInModal.selectedItem.declaration!.consultant!.prenomNom}</span>}
                         show={true}
                         onCloseCallback={() => setItemInModal(null)}
                         fullscreen
                    >
                         <ConsultantDeclarationDetails
                              declaration={itemInModal.selectedItem.declaration as IConsultantDeclarationMensuelleModel}
                              nbJoursTravailles={itemInModal.selectedItem.nbJoursTravailles}
                              handleDemandeRectificationCallback={handleDemandeRectificationCallback}
                              handleValidationCallback={handleValidationCallback}
                              handleValidationDemandeRegulCallback={handleValidationDemandeRegulCallback}
                              handleRejetDemandeRegulCallback={handleRejetDemandeRegulCallback}
                              handleReviewCallback={handleReviewCallback}
                              handleCancelReviewCallback={handleCancelReviewCallback}
                              defaultStep={
                                   ![CONST_CONSULTANT_STATUT_ST_PORTAGE, CONST_CONSULTANT_STATUT_ST_SOCIETE_SERVICE].includes(
                                        itemInModal.selectedItem.declaration?.consultant?.statut!
                                   )
                                        ? "cra"
                                        : "factures"
                              }
                         />
                    </MyModal>
               )}

               {/* Modal with suivi */}
               {suiviInModal && (
                    <MyModal title={<span>Suivi de la déclaration de {suiviInModal.consultant!.prenomNom}</span>} show={true} onCloseCallback={() => setSuiviInModal(undefined)}>
                         <ConsultantDeclarationSuivi declaration={suiviInModal} />
                    </MyModal>
               )}
          </ConsultantsDeclarationsListingContext.Provider>
     )
}
export default ConsultantsDeclarationsListing
